import type { SxProps, Theme } from "@mui/material";
import { Box, ListItem, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { useSelector } from "react-redux";
import CloseIconButton from "@entrilia/shared/components/CloseIconButton";
import type { GroupingField } from "../../../../../../../../api/types/svTypes.ts";
import { AreaItemType } from "../../../../../../../../api/types/svTypes.ts";
import { selectTabularFields } from "../../../../store/selectors/fieldsStateSelectors";

interface Props {
  field: GroupingField;
  sx?: SxProps<Theme>;
  readonlyMode?: boolean;
  onRemoveItem?: (field: GroupingField) => void;
  onEndMoveItem?: (field: GroupingField) => void;
  onDragMove?: (field: GroupingField, replaceField: GroupingField) => void;
}

export function GroupFieldItem({ field, sx, onRemoveItem, onEndMoveItem, onDragMove, readonlyMode }: Props) {
  const fieldsAreaValues = useSelector(selectTabularFields);
  const dimension = useMemo(() => fieldsAreaValues.find((f) => f.config.guid === field.id), [field, fieldsAreaValues]);

  const [{ isDragging }, drag, dragPreview] = useDrag(
    () => ({
      type: AreaItemType.GROUPS,
      item: field,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item, monitor) => {
        if (monitor.didDrop()) {
          onEndMoveItem?.call(null, item);
        }
      },
      canDrag: () => !readonlyMode,
    }),
    [field, readonlyMode]
  );

  const [, drop] = useDrop(
    () => ({
      accept: AreaItemType.GROUPS,
      hover(item: GroupingField) {
        if (item.id !== field.id) {
          onDragMove?.call(null, item, field);
        }
      },
    }),
    [field]
  );

  const onRef = useCallback(
    (node: HTMLLIElement | null) => {
      drag(drop(node));
    },
    [drag, drop]
  );

  const handleRemoveItem = useCallback(() => {
    onRemoveItem?.call(null, field);
  }, [onRemoveItem, field]);

  useEffect(() => {
    dragPreview(getEmptyImage());
  }, [dragPreview]);

  return (
    <ListItem
      role="Handle"
      ref={onRef}
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: ".5rem",
        height: "32px",
        borderRadius: "2px",
        p: "5px 0",
        opacity: isDragging ? 0 : 1,
        backgroundColor: "background.grey",
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "row",
          alignItems: "center",
          border: "1px solid",
          borderColor: readonlyMode ? "action.disabled" : "text.disabled",
          borderRadius: "2px",
          backgroundColor: "background.paper",
          p: "0 5px",
          "&:hover": {
            cursor: readonlyMode ? "default" : "grab",
          },
          minHeight: "32px",
          overflow: "hidden",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "flex",
            flexGrow: 1,
            color: (theme) => (readonlyMode ? theme.palette.text.disabled : theme.palette.text.primary),
          }}
          noWrap
        >
          {dimension?.config.customLabel || dimension?.meta.caption}
        </Typography>
        {!readonlyMode && <CloseIconButton onClick={handleRemoveItem} />}
      </Box>
    </ListItem>
  );
}

export default GroupFieldItem;
